import { ReactElement, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultActionMethods, WindowNames } from '@cashier/cashier-model';
import {
  convertToUrl,
  eventBus,
  EventFactory,
  ProviderWindowService,
} from '@cashier/cashier-shared';
import { Loader } from '@cashier/cashier-ui';

import { useHiddenFormContext } from '../../../contexts';
import { FallbackLink } from '../../fallbackLink';

export interface PaymentProgressProps {
  applyAction: (
    sendRequest: (
      method: ResultActionMethods,
      url: string,
      data: { [key: string]: string },
    ) => void,
    renderContent: (content: string) => void,
  ) => void;
  paymentMethodName: string;
  loadingPageUrl: string;
  isWebView: boolean;
  handleFallbackInitiated: (submitForm: () => void) => void;
}

export const PaymentProgress = ({
  applyAction,
  isWebView,
  loadingPageUrl,
  handleFallbackInitiated,
  paymentMethodName,
}: PaymentProgressProps): ReactElement => {
  const { t } = useTranslation();

  const { submitForm } = useHiddenFormContext();

  const sendRequest = useCallback(
    (
      method: ResultActionMethods,
      url: string,
      data: { [key: string]: string },
    ) => {
      if (ResultActionMethods.GET === method) {
        ProviderWindowService.redirect(convertToUrl(url, data));

        return;
      }

      if (isWebView) {
        eventBus.emit(EventFactory.submitForm(url, method.toString(), data));
      } else {
        ProviderWindowService.initialize(WindowNames.Window, loadingPageUrl);

        submitForm();
      }
    },
    [isWebView, loadingPageUrl, submitForm],
  );
  const renderContent = (output: string) => {
    ProviderWindowService.initialize(WindowNames.Window, 'about:blank');

    ProviderWindowService.writeContent(WindowNames.Window, output);
  };

  useEffect(() => {
    applyAction(sendRequest, renderContent);
  }, [applyAction, sendRequest]);

  return (
    <div className="shown shown__platform">
      <div className="processing-result processing-result__platform">
        <p
          className="processing-result__message processing-result__message__platform"
          data-test="processing-message"
        >
          {t('waitingFor.message', { paymentMethod: paymentMethodName })}
        </p>

        <div className="processing-result__loaderContainer" data-test="loader">
          <Loader theme="colored" size="small" />
        </div>
      </div>
      <FallbackLink
        version="v2"
        handleClick={() => handleFallbackInitiated(submitForm)}
      />
    </div>
  );
};
